import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { toast } from "react-hot-toast";
import { forgotPassword } from "../services/auth";
import { Eye, EyeOff, Mail, ArrowRight, X } from "lucide-react";

const ILLUSTRATION_SLIDES = [
  {
    src: "/login/login-page.svg",
    alt: "ATIAM College Management Illustration",
  },
  {
    src: "/login/graduation.svg",
    alt: "ATIAM Graduation Illustration",
  },
  {
    src: "/login/languages.png",
    alt: "ATIAM World Languages Illustration",
  },
];

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [randomSlideIndex] = useState(() => {
    if (ILLUSTRATION_SLIDES.length === 0) return 0;
    return Math.floor(Math.random() * ILLUSTRATION_SLIDES.length);
  });
  const { login, loading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const success = await login(email, password);
      if (success) {
        toast.success("Login successful!");
      } else {
        toast.error("Login failed. Please check your credentials.");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage =
        error.message || "Login failed. Please check your credentials.";
      toast.error(errorMessage);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setIsResetting(true);
    try {
      const response = await forgotPassword(resetEmail);
      if (response.success) {
        toast.success("Password reset link sent to your email!");
        setShowForgotPassword(false);
        setResetEmail("");
      } else {
        toast.error(response.message || "Failed to send reset email");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col items-center justify-start md:justify-center py-6 md:py-8 px-0 md:p-6">
      <div className="w-full max-h-screen overflow-y-auto md:max-w-5xl bg-white/95 backdrop-blur-xl md:rounded-2xl border-0 md:border border-slate-200/60 shadow-none md:shadow-2xl flex flex-col md:flex-row">
        {/* ── Left / Top Column: Illustration & Branding ── */}
        {/* Mobile: compact top banner  |  md+: full left column */}
        <div className="w-full md:w-1/2 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative flex flex-col items-center justify-center px-8 py-8 md:p-12 overflow-hidden border-b md:border-b-0 md:border-r border-slate-200/60">
          {/* Subtle accent blobs (desktop only) */}
          <div className="hidden md:block absolute top-[-60px] right-[-60px] w-64 h-64 bg-blue-200/30 rounded-full blur-3xl" />
          <div className="hidden md:block absolute bottom-[-40px] left-[-40px] w-56 h-56 bg-indigo-200/25 rounded-full blur-3xl" />

          {/* Logo */}
          <div className="relative z-10 flex flex-col items-center text-center mb-6 md:mb-8">
            <div className="bg-white rounded-2xl p-3 md:p-4 shadow-lg mb-3 md:mb-4 border border-slate-200/60">
              <img
                src="/school-logo.png"
                alt="ATIAM College Logo"
                className="h-10 md:h-14 w-auto"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          </div>

          {/* Illustrations */}
          <div className="relative z-10 w-full flex justify-center mb-6 md:mb-8">
            <img
              src={ILLUSTRATION_SLIDES[randomSlideIndex].src}
              alt={ILLUSTRATION_SLIDES[randomSlideIndex].alt}
              className="w-full max-w-[180px] md:max-w-xs drop-shadow-lg transition-opacity duration-500"
            />
          </div>

          {/* Quote */}
          <div className="relative z-10 w-full max-w-xs text-center">
            <p className="text-slate-500 text-sm font-semibold italic leading-relaxed">
              &ldquo;Ranked the best for a reason&rdquo;
            </p>
          </div>
        </div>

        {/* ── Right / Bottom Column: Login Form ── */}
        <div className="w-full md:w-1/2 flex flex-col justify-center px-6 py-8 md:px-10 lg:px-12">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Welcome back
            </h2>
            <p className="text-slate-600 font-semibold mt-1 text-sm">
              Sign in to your account to continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-bold text-slate-900 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@atiamcollege.com"
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-bold text-slate-900 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-12 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="btn-transparent absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-green-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Forgot password link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="btn-transparent text-sm font-bold text-blue-600 hover:text-indigo-600 transition-colors duration-200"
              >
                Forgot password?
              </button>
            </div>
          </form>

          {/* Divider + register link */}
          <div className="mt-6 sm:mt-8 pt-6 border-t border-slate-200">
            <p className="text-center text-sm font-semibold text-slate-600">
              New to ATIAM?{" "}
              <a
                href="/register/ecourse"
                className="font-bold text-blue-600 hover:text-indigo-600 transition-colors duration-200"
              >
                Enroll in E-Courses →
              </a>
            </p>
            <p className="text-center mt-3">
              <a
                href="https://atiamcollege.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors duration-200"
              >
                ← Back to ATIAM College website
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl w-full max-w-md overflow-hidden">
            {/* Modal header */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  Reset Password
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmail("");
                }}
                className="p-2 bg-white border-2 border-slate-200 rounded-xl hover:shadow-lg hover:scale-110 transition-all duration-300"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-6">
              <p className="text-sm font-semibold text-slate-600 mb-5">
                Enter your email address and we'll send you a link to reset your
                password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="resetEmail"
                    className="block text-sm font-bold text-slate-900 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      placeholder="Enter your email address"
                      className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail("");
                    }}
                    className="flex-1 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isResetting}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isResetting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
